/* Editor — bulk-run selected manuscripts through an AI model (Oct-2026). */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Sparkles, X } from 'lucide-react';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function BulkAiRunBar({ selectedIds, onClear, onDone }) {
  const [aiModels, setAiModels] = useState([]);
  const [modelId,  setModelId]  = useState('claude-opus-4-7');
  const [mode,     setMode]     = useState('rewrite');
  const [job,      setJob]      = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    http.get('/admin/models').then((r) => setAiModels(r.data?.models || [])).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, []);

  const run = async () => {
    const chosen = aiModels.find((m) => m.id === modelId);
    try {
      const { data } = await http.post('/editor/bulk-ai-run', {
        review_ids: selectedIds, mode,
        provider: chosen?.provider || 'anthropic',
        model:    chosen?.model || modelId,
      });
      setJob({ id: data.job_id, status: 'running', done_chapters: 0, total_chapters: 0 });
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await http.get(`/editor/bulk-ai-run/${data.job_id}`);
          setJob(j);
          if (j.status === 'completed') {
            clearInterval(pollRef.current);
            const errors = (j.results || []).filter((r) => r.error).length;
            const suggestions = (j.results || []).filter((r) => r.suggestion_id).length;
            toast.success(
              mode === 'rewrite'
                ? `Bulk run done · ${suggestions} suggestion(s) sent to authors${errors ? ` · ${errors} failed` : ''}`
                : `Bulk run done · ${j.done_chapters} chapter(s) evaluated${errors ? ` · ${errors} failed` : ''}`,
            );
            setJob(null);
            onDone?.();
          }
        } catch {
          clearInterval(pollRef.current);
          setJob(null);
          toast.error('Lost connection to the bulk run — check the queue in a moment.');
        }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start bulk run.');
    }
  };

  if (!selectedIds.length && !job) return null;

  return (
    <div data-testid="bulk-ai-run-bar"
         className="flex flex-wrap items-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2">
      {job ? (
        <div className="flex items-center gap-2 text-[12px]" data-testid="bulk-ai-run-progress">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
          Running {mode} on {selectedIds.length} manuscript(s)… {job.done_chapters}/{job.total_chapters || '?'} chapters
        </div>
      ) : (
        <>
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <span className="text-[12px] font-medium">{selectedIds.length} selected</span>
          <select value={mode} onChange={(e) => setMode(e.target.value)}
                  data-testid="bulk-ai-mode-select"
                  className="h-7 rounded-sm border border-border bg-background px-1.5 text-[11px]">
            <option value="rewrite">Rewrite each chapter → send to authors</option>
            <option value="evaluate">Evaluate only (editor notes)</option>
          </select>
          {aiModels.length > 0 && (
            <select value={modelId} onChange={(e) => setModelId(e.target.value)}
                    data-testid="bulk-ai-model-select"
                    className="h-7 rounded-sm border border-border bg-background px-1.5 text-[11px]">
              {aiModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          )}
          <button onClick={run} data-testid="bulk-ai-run-btn"
                  className="h-7 rounded-md bg-primary px-3 text-[11px] font-semibold text-primary-foreground hover:opacity-90">
            Run on {selectedIds.length}
          </button>
          <button onClick={onClear} data-testid="bulk-ai-clear-btn"
                  className="h-7 rounded-md border border-border bg-background px-2 text-[11px] hover:bg-secondary">
            <X className="h-3 w-3" />
          </button>
        </>
      )}
    </div>
  );
}
